/**
 * arch.js — scroll choreography for the "Architects & designers" experience page.
 *
 * Loaded only by the arch-* sections. Touches nothing else in the theme.
 *
 * Design notes
 * ------------
 * - No animation library. Scenes write numbers (0..1) into CSS custom
 *   properties. CSS does the rest, so everything stays on the compositor and
 *   the bundle stays ~2KB gzipped.
 * - Every piece of state is recomputed from scroll position on each frame, so
 *   it is identical whether the visitor scrolls slowly, flings, or scrolls back
 *   up. Nothing depends on having observed a transition.
 * - Nothing here is time-based. The staged sections used to flip a class at a
 *   threshold and let a CSS transition play out on its own clock, so a small
 *   trackpad movement did nothing until it crossed a boundary and five
 *   products felt like five snaps. Every step's presence is now a continuous
 *   function of scroll: the picture answers the finger, with no catching up
 *   after the scroll stops.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
  const smooth = (t) => t * t * (3 - 2 * t);

  /* ------------------------------------------------------------------ *
   * Catalogue CTA — the one thing that must never break.
   *
   * click -> _klOnsite.push(['openForm', id]) -> Klaviyo popup "Architekci
   * i projektanci" -> list Wuj7EJ -> flow YkXas5 -> catalogue email.
   * ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-arch-catalog]');
    if (!trigger) return;
    const formId = trigger.getAttribute('data-arch-catalog');
    if (!formId) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  });

  /* The scroll cue is a real control: it says "scroll" and it also scrolls. */
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-arch-cue]')) return;
    window.scrollTo({ top: window.scrollY + window.innerHeight * 0.9, behavior: reduced.matches ? 'auto' : 'smooth' });
  });

  /* ------------------------------------------------------------------ *
   * Easing for the signature pull-back.
   *
   * The camera has to START MOVING IMMEDIATELY. A smootherstep is flat at
   * both ends: at 12% of the scroll the scale had travelled 0.7% of its
   * range, so the visitor pushed the trackpad and nothing happened. This
   * curve is steep at the start and settles long — the edge of the fixture is
   * already appearing within the first few percent.
   *   p 0.03 -> 3.77   p 0.10 -> 2.99   p 0.20 -> 2.25   p 0.50 -> 1.27
   * ------------------------------------------------------------------ */
  const revealCurve = (p) => 1 - Math.pow(1 - clamp01(p), 2.2);

  /* How much of one step's share of the track is spent handing over to the
     next one. Long enough to feel like a move, short enough that two products
     are never both legible. */
  const HANDOVER = 0.5;

  // An application link goes to the centre of that photograph's existing
  // scroll interval. Manual scrolling keeps exactly the same choreography.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-arch-app-link]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const track = link.closest('[data-arch-track="apps"]');
    if (!track) return;
    const plates = track.querySelectorAll('.arch-apps__plate');
    const index = Number(link.dataset.archAppLink);
    if (!Number.isInteger(index) || index < 0 || index >= plates.length) return;
    event.preventDefault();

    if (reduced.matches) {
      plates[index].scrollIntoView({ block: 'start', behavior: 'auto' });
      track.querySelectorAll('[data-arch-app-link]').forEach((item) => {
        item.setAttribute('aria-current', String(item === link));
      });
      return;
    }

    const rect = track.getBoundingClientRect();
    const travel = Math.max(0, rect.height - window.innerHeight);
    const progress = (index + (1 - HANDOVER) / 2) / (plates.length - HANDOVER);
    window.scrollTo({ top: window.scrollY + rect.top + travel * progress, behavior: 'smooth' });
  });

  let scenes = [], pending = [], grounds = [], nav = null, navGate = null, ticking = false;
  const DARK = 'arch-reveal arch-lookup arch-detail arch-steps arch-apps arch-cct arch-finale'.split(' ');

  function collect() {
    scenes = Array.from(document.querySelectorAll('[data-arch-track]'))
      .map((track) => {
        const sticky = track.querySelector('[data-arch-sticky]');
        if (!sticky) return null;
        // Several groups (plates, names, ticks) share one index, so the step
        // number is carried on the attribute rather than inferred from order.
        const steps = Array.from(track.querySelectorAll('[data-arch-step]'))
          .map((el) => ({ el, i: +el.dataset.archStep || 0, on: -1 }));
        const count = steps.length ? Math.max(...steps.map((s) => s.i)) + 1 : 0;
        return {
          track, sticky, steps, count, activeStep: -1,
          kind: track.getAttribute('data-arch-track') || 'track',
          zoomFrom: parseFloat(track.getAttribute('data-zoom-from')) || 1,
          zoomTo: parseFloat(track.getAttribute('data-zoom-to')) || 1
        };
      })
      .filter(Boolean);

    pending = Array.from(document.querySelectorAll('[data-arch-in]:not(.is-in)'));

    grounds = Array.from(
      document.querySelectorAll('.arch-reveal, .arch-statement, .arch-lookup, .arch-detail, .arch-steps, .arch-apps, .arch-cct, .arch-partner, .arch-finale')
    ).map((el) => ({ el, dark: DARK.some((c) => el.classList.contains(c)) }));
  }

  /* Presence of step k, as a continuous function of how far through the track
     we are. Full for the middle of its own band, crossing over with its
     neighbour across a window of HANDOVER. */
  function presenceOf(pos, k) {
    const inn = clamp01((pos - (k - HANDOVER)) / HANDOVER);
    const out = clamp01((pos - (k + 1 - HANDOVER)) / HANDOVER);
    return smooth(inn) * (1 - smooth(out));
  }

  function applyScene(scene) {
    const rect = scene.track.getBoundingClientRect();
    const vh = window.innerHeight;

    // Skip work for scenes that are nowhere near the viewport.
    if (rect.bottom < -vh || rect.top > vh * 2) return;

    const travel = rect.height - vh;
    const p = travel > 0 ? clamp01(-rect.top / travel) : 0;
    scene.sticky.style.setProperty('--p', p.toFixed(4));

    if (scene.kind === 'reveal') {
      const e = revealCurve(p / 0.85);
      const scale = scene.zoomFrom * Math.pow(scene.zoomTo / scene.zoomFrom, e);
      scene.sticky.style.setProperty('--scale', scale.toFixed(4));
    }

    if (!scene.count) return;

    const n = scene.count;
    // Step k is fully present for pos in [k, k + 1 - HANDOVER]. Mapping p
    // across [0, (n - 1) + (1 - HANDOVER)] gives every step that same hold.
    // The old `+ 0.5` started where step 0 begins handing over, so the first
    // product was already dissolving on arrival.
    const pos = clamp01(p) * (n - 1 + (1 - HANDOVER));
    let best = 0, bestOn = -1;

    for (const s of scene.steps) {
      const on = presenceOf(pos, s.i);
      if (on > bestOn) { bestOn = on; best = s.i; }
      if (Math.abs(on - s.on) > 0.004) {
        s.el.style.setProperty('--on', on.toFixed(3));
        s.on = on;
      }
    }

    if (best !== scene.activeStep) {
      for (const s of scene.steps) s.el.classList.toggle('is-active', s.i === best);
      scene.sticky.setAttribute('data-step', String(best + 1));
      scene.track.querySelectorAll('[data-arch-app-link]').forEach((link) => {
        link.setAttribute('aria-current', String(Number(link.dataset.archAppLink) === best));
      });
      scene.activeStep = best;
    }
  }

  function applyReveals() {
    if (!pending.length) return;
    const line = window.innerHeight * 0.88;
    let write = 0;
    for (let i = 0; i < pending.length; i++) {
      const el = pending[i];
      if (el.getBoundingClientRect().top < line) el.classList.add('is-in');
      else pending[write++] = el;
    }
    pending.length = write;
  }

  function updateNav() {
    if (!nav) return;
    const gateBottom = navGate ? navGate.getBoundingClientRect().bottom : 0;
    nav.classList.toggle('is-on', gateBottom < window.innerHeight * 0.5);

    const line = (nav.offsetHeight || 60) * 0.6;
    for (const g of grounds) {
      const r = g.el.getBoundingClientRect();
      if (r.top <= line && r.bottom > line) {
        nav.dataset.ground = g.dark ? 'dark' : 'light';
        break;
      }
    }
  }

  function frame() {
    ticking = false;
    for (const s of scenes) applyScene(s);
    applyReveals();
    updateNav();
  }

  function schedule() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function start() {
    nav = document.querySelector('[data-arch-nav]');
    navGate = document.querySelector('[data-arch-nav-gate]');

    // Under reduced motion CSS already holds everything in its end state.
    if (reduced.matches) {
      if (nav) nav.classList.add('is-on');
      document.querySelectorAll('[data-arch-step]').forEach((el) => {
        el.style.removeProperty('--on');
        el.classList.toggle('is-active', (+el.dataset.archStep || 0) === 0);
      });
      return;
    }

    collect();
    frame();
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => { collect(); schedule(); }, { passive: true });
  window.addEventListener('orientationchange', () => { collect(); schedule(); }, { passive: true });
  window.addEventListener('load', schedule);
  reduced.addEventListener('change', start);

  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', start);
  document.addEventListener('shopify:section:reorder', start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
